import { FileWriter } from '@/platform'
import { BrowserFileUtils } from '@/platform/utils/BrowserFileUtils'
import BitmapFontData, {
  BitmapFontFile,
  BitmapFontGlyph,
  BitmapFontPackage,
} from '@/model/BitmapFont/BitmapFontData'
import type MainProject from '@/model/MainProject'
import type EventBase from '@/model/EventClass/EventBase'
import EventID from '@/model/EventClass/EventID'
import EventText from '@/model/EventClass/EventText'
import EventSendAirBubble from '@/model/EventClass/EventSendAirBubble'
import EventTextDif from '@/model/EventClass/EventTextDif'
import EventTextDifEx from '@/model/EventClass/EventTextDifEx'
import EventTextDifEx2 from '@/model/EventClass/EventTextDifEx2'
import EventTextChoice from '@/model/EventClass/EventTextChoice'
import EventString from '@/model/EventClass/EventString'
import EventCallMenu from '@/model/EventClass/EventCallMenu'
import EventCallScene from '@/model/EventClass/EventCallScene'
import type MapInfo from '@/model/MapInfo'
import type DataFileMap from '@/model/Resource/DataFileMap'
import {
  ensureFontLoadedFromVirtualResource,
} from '@/features/project-font/runtime'
import {
  formatCanvasFontFamily,
  measureChengguangBitmapGlyph,
} from '@/features/bitmap-font/metrics'
import {
  resolveBitmapFontRasterSize,
} from '@/features/bitmap-font/bitmapFontPadding'
import {
  buildFontListBytes,
  buildSaveLoadListBytes,
  collectBitmapFontTargets,
  resolveMainFontSpec,
  toBitmapFontPackageKey,
} from '@/features/bitmap-font/fontSpec'
import { clampBitmapFontSize } from '@/features/bitmap-font/fontSize'
import { stripEmoji } from '@/utils/plainTextInput'

export { clampBitmapFontSize } from '@/features/bitmap-font/fontSize'
export { toBitmapFontPackageKey } from '@/features/bitmap-font/fontSpec'

const ATLAS_MAX_WIDTH = 1024
const ATLAS_MAX_HEIGHT = 1024
/** 剧情与存读档位图字体均包含的常用 ASCII 与基础标点。 */
const DEFAULT_BITMAP_FONT_CHARS =
  '0123456789abcdefghijklmnopqrstuvwxyzABCDEFGHIJKLMNOPQRSTUVWXYZ!@#$%^&*()-_=+/□:七… '
const SAVE_LOAD_BITMAP_FONT_CHARS = DEFAULT_BITMAP_FONT_CHARS.replace('七…', '')

function uniqueChars(input: string): string {
  const ordered: string[] = []
  const seen = new Set<string>()
  for (const char of Array.from(input || '')) {
    if (!char || char === '\r' || char === '\n') continue
    if (seen.has(char)) continue
    seen.add(char)
    ordered.push(char)
  }
  return ordered.join('')
}

function appendChars(buffer: string[], value: string): void {
  if (!value) return
  const sanitized = stripEmoji(value)
  if (!sanitized) return
  buffer.push(sanitized)
}

function collectEventStrings(events: EventBase[] | null | undefined, output: string[]): void {
  if (!Array.isArray(events)) return
  for (const event of events) {
    if (!event) continue
    switch (event.Code) {
      case EventID.Text: {
        const typed = event as EventText
        appendChars(output, typed.RoleName || typed.Parameter?.[0] || '')
        appendChars(output, typed.Content || typed.Parameter?.[2] || '')
        break
      }
      case EventID.SendAirBubble: {
        const typed = event as EventSendAirBubble
        appendChars(output, typed.Name)
        if (typed.MessageType === 1) {
          appendChars(output, typed.MessageConten)
        }
        break
      }
      case EventID.TextDif: {
        const typed = event as EventTextDif
        for (const item of typed.TextDifChoice) appendChars(output, item)
        break
      }
      case EventID.TextDifEx: {
        const typed = event as EventTextDifEx
        for (const item of typed.ChoiceText) appendChars(output, item)
        break
      }
      case EventID.TextDifEx2: {
        const typed = event as EventTextDifEx2
        for (const item of typed.ChoiceText) appendChars(output, item)
        break
      }
      case EventID.TextChoice: {
        const typed = event as EventTextChoice
        appendChars(output, typed.Content)
        break
      }
      case EventID.String: {
        const typed = event as EventString
        appendChars(output, typed.Content)
        break
      }
      case EventID.CallMenu: {
        const typed = event as EventCallMenu
        if (typed.Number === 10014) {
          const parts = typed.Input.split(',')
          for (let index = 0; index < parts.length; index += 3) {
            appendChars(output, parts[index] ?? '')
          }
        }
        break
      }
      case EventID.CallScene: {
        const typed = event as EventCallScene
        for (const item of typed.inputParams) {
          if (item.type === '6') {
            appendChars(output, item.value)
          }
        }
        break
      }
      default:
        break
    }
  }
}

/** 遍历工程剧情树全部节点（rootMaps + Maps，按 Id 去重）。 */
function forEachStoryMap(project: MainProject, visit: (map: MapInfo) => void): void {
  const visited = new Set<number>()
  const walk = (map: MapInfo): void => {
    if (!map || visited.has(map.Id)) return
    visited.add(map.Id)
    visit(map)
    for (const child of map.Childs ?? []) {
      walk(child)
    }
  }
  const roots = project.storyTree.rootMaps?.length ? project.storyTree.rootMaps : project.storyTree.Maps
  for (const map of roots) {
    walk(map)
  }
  for (const map of project.storyTree.Maps) {
    walk(map)
  }
}

function collectStoryTreeStrings(project: MainProject, output: string[]): void {
  forEachStoryMap(project, (map) => {
    appendChars(output, map.Name)
    collectEventStrings(project.storyTree.loadEvent(map.Id), output)
  })
}

/** 存读档独立位图字体包所需字符：常用符号 + 剧情名称（不含事件正文）。 */
function collectSaveLoadBitmapFontText(project: MainProject): string {
  const chunks: string[] = [SAVE_LOAD_BITMAP_FONT_CHARS]
  forEachStoryMap(project, (map) => {
    appendChars(chunks, map.Name)
  })
  return uniqueChars(chunks.join(''))
}

/**
 * 收集剧情位图字体所需的全部字符（事件文本、系统文案、剧情名等）。
 * @param project 当前工程
 */
export function collectProjectBitmapFontText(project: MainProject): string {
  const chunks: string[] = [DEFAULT_BITMAP_FONT_CHARS]
  appendChars(chunks, project.systemData.totalSetting.AuthorWords)
  collectStoryTreeStrings(project, chunks)

  for (const item of project.systemData.CG.CGs) {
    appendChars(chunks, item.Name)
    appendChars(chunks, item.Message)
  }
  for (const item of project.systemData.BGM.BGMs) {
    appendChars(chunks, item.Name)
    appendChars(chunks, item.Message)
  }
  appendChars(chunks, project.systemData.BGM.NoName)

  for (const ui of project.systemData.customUI.CUIList) {
    collectEventStrings(ui.LoadEvent, chunks)
    collectEventStrings(ui.AfterEvent, chunks)
    for (const control of ui.ControlList) {
      collectEventStrings(control.EventList, chunks)
    }
  }

  for (const button of project.systemData.floatButton.FBtnList) {
    collectEventStrings(button.EventList, chunks)
  }

  return uniqueChars(chunks.join(''))
}

/**
 * 3.0 `MAKER_BITMAP_FONT`：灰字、alphabetic 基线，字号已扣 padding。
 */
export function paintChengguangBitmapGlyph(
  ctx: CanvasRenderingContext2D,
  char: string,
  x: number,
  y: number,
  fontSize: number,
  fontFamily: string,
): void {
  ctx.textBaseline = 'alphabetic'
  ctx.font = `${Math.max(1, fontSize)}px ${formatCanvasFontFamily(fontFamily)}`
  ctx.fillStyle = 'rgb(130, 130, 130)'
  ctx.fillText(char || 'NULL', x, y)
}

/**
 * 按 3.0 格子把单字画进 cell，供增量补字和预览缺字回退。
 */
export function rasterizeBitmapFontGlyph(params: {
  char: string
  fontFamily: string
  fontSize: number
  cellWidth: number
  cellHeight: number
  aboveLine: number
}): { bitmap: HTMLCanvasElement } {
  const bitmapWidth = Math.max(1, params.cellWidth)
  const bitmapHeight = Math.max(1, params.cellHeight)
  const bitmap = document.createElement('canvas')
  bitmap.width = bitmapWidth
  bitmap.height = bitmapHeight
  const bitmapCtx = bitmap.getContext('2d')
  if (!bitmapCtx) {
    throw new Error('浏览器不支持 2D Canvas，无法栅格化位图字体')
  }
  bitmapCtx.clearRect(0, 0, bitmap.width, bitmap.height)
  paintChengguangBitmapGlyph(
    bitmapCtx,
    params.char,
    0,
    Math.max(0, params.aboveLine),
    params.fontSize,
    params.fontFamily,
  )
  return { bitmap }
}

function decodeBase64ToBytes(base64: string): Uint8Array {
  const binary = atob(base64)
  const bytes = new Uint8Array(binary.length)
  for (let index = 0; index < binary.length; index += 1) {
    bytes[index] = binary.charCodeAt(index)
  }
  return bytes
}

function canvasToBytes(canvas: HTMLCanvasElement): Promise<Uint8Array> {
  return new Promise((resolve, reject) => {
    try {
      canvas.toBlob(async (blob) => {
        if (blob) {
          resolve(new Uint8Array(await BrowserFileUtils.blobToArrayBuffer(blob)))
          return
        }
        try {
          const dataUrl = canvas.toDataURL('image/png')
          const base64 = dataUrl.split(',', 2)[1] || ''
          if (!base64) {
            reject(new Error(`位图字体页面导出失败：空图像数据（${canvas.width}x${canvas.height}）`))
            return
          }
          resolve(decodeBase64ToBytes(base64))
        } catch {
          reject(new Error(`位图字体页面导出失败：无法导出 PNG（${canvas.width}x${canvas.height}）`))
        }
      }, 'image/png')
    } catch {
      reject(new Error(`位图字体页面导出失败：浏览器导出接口异常（${canvas.width}x${canvas.height}）`))
    }
  })
}

function buildXfiBytes(params: {
  rows: number
  cols: number
  cellWidth: number
  cellHeight: number
  glyphs: BitmapFontGlyph[]
}): Uint8Array {
  const writer = new FileWriter()
  writer.writeInt(params.rows)
  writer.writeInt(params.cols)
  writer.writeInt(params.cellWidth)
  writer.writeInt(params.cellHeight)
  writer.writeInt(params.glyphs.length)
  for (const glyph of params.glyphs) {
    writer.writeInt(glyph.index)
    writer.writeInt(glyph.width)
    writer.writeInt(glyph.height)
    writer.writeUStringRaw(String(glyph.char || '').substring(0, 1))
  }
  return writer.toUint8Array()
}

function collectMergedBitmapFontText(params: {
  project: MainProject
  previousExtraText?: string
  extraText?: string
}): string {
  return uniqueChars(
    [
      collectProjectBitmapFontText(params.project),
      params.previousExtraText ?? '',
      params.extraText ?? '',
    ].join(''),
  )
}

/** 工程所需且尚未生成过的字符（按 mergedText 首次出现顺序）。 */
function listMissingChars(mergedText: string, glyphs: BitmapFontGlyph[]): string {
  const existingChars = new Set<string>()
  for (const glyph of glyphs) {
    if (glyph.char) existingChars.add(glyph.char)
  }
  return Array.from(uniqueChars(mergedText)).filter((char) => !existingChars.has(char)).join('')
}

function canReuseBitmapFontPackage(
  existing: BitmapFontPackage,
  sourceFontKey: string,
  fontName: string,
  fontSize: number,
  rasterFontSize: number,
): boolean {
  return (
    existing.sourceFontKey === sourceFontKey
    && existing.fontName === fontName
    && existing.fontSize === fontSize
    && existing.rasterFontSize === rasterFontSize
    && existing.aboveLine > 0
    && existing.cellWidth > 0
    && existing.cellHeight > 0
    && existing.pageLayoutFixed === true
    && existing.glyphs.length > 0
    && existing.files.some((item) => item.kind === 'xfi')
  )
}

function measurePackageGlyphs(
  chars: string[],
  fontFamily: string,
  rasterFontSize: number,
  fallbackSize: number,
): {
  cellWidth: number
  cellHeight: number
  aboveLine: number
  glyphs: ReturnType<typeof measureChengguangBitmapGlyph>[]
} {
  let cellWidth = 0
  let cellHeight = 0
  let aboveLine = 0
  const glyphs = chars.map((char) => {
    const glyph = measureChengguangBitmapGlyph(char, fontFamily, rasterFontSize)
    cellWidth = Math.max(cellWidth, glyph.width)
    cellHeight = Math.max(cellHeight, glyph.height)
    aboveLine = Math.max(aboveLine, glyph.aboveLine)
    return glyph
  })
  return {
    cellWidth: Math.max(1, cellWidth || fallbackSize),
    cellHeight: Math.max(1, cellHeight || fallbackSize),
    aboveLine: aboveLine || fallbackSize,
    glyphs,
  }
}

function charsNeedLargerCell(
  chars: string,
  existing: BitmapFontPackage,
  fontFamily: string,
  rasterFontSize: number,
): boolean {
  for (const char of Array.from(chars)) {
    const glyph = measureChengguangBitmapGlyph(char, fontFamily, rasterFontSize)
    if (glyph.width > existing.cellWidth) return true
    if (glyph.height > existing.cellHeight) return true
    if (glyph.aboveLine > existing.aboveLine) return true
  }
  return false
}

function resolveGlyphXfiHeight(
  height: number,
  underlineSize: number,
  aboveLine: number,
): number {
  return Math.max(height, underlineSize + aboveLine)
}

function paintGlyphInCell(
  ctx: CanvasRenderingContext2D,
  char: string,
  col: number,
  row: number,
  cellWidth: number,
  cellHeight: number,
  aboveLine: number,
  rasterFontSize: number,
  fontFamily: string,
): void {
  paintChengguangBitmapGlyph(
    ctx,
    char,
    col * cellWidth,
    row * cellHeight + aboveLine,
    rasterFontSize,
    fontFamily,
  )
}

function sortPackagePageFiles(files: BitmapFontFile[], fileBase: string): BitmapFontFile[] {
  return files
    .filter((item) => item.kind === 'page' && item.fileName.toLowerCase().startsWith(`${fileBase.toLowerCase()}$`))
    .slice()
    .sort((a, b) => {
      const pageA = Number(/\.(\d+)\.png$/i.exec(a.fileName)?.[1] ?? 0)
      const pageB = Number(/\.(\d+)\.png$/i.exec(b.fileName)?.[1] ?? 0)
      return pageA - pageB
    })
}

function toArrayBufferFromBytes(bytes: Uint8Array): ArrayBuffer {
  return bytes.buffer.slice(bytes.byteOffset, bytes.byteOffset + bytes.byteLength) as ArrayBuffer
}

async function bytesToCanvas(bytes: Uint8Array): Promise<HTMLCanvasElement> {
  const blob = new Blob([toArrayBufferFromBytes(bytes)], { type: 'image/png' })
  const url = URL.createObjectURL(blob)
  try {
    const image = new Image()
    await new Promise<void>((resolve, reject) => {
      image.onload = () => resolve()
      image.onerror = () => reject(new Error('位图字体页图像解码失败'))
      image.src = url
    })
    const canvas = document.createElement('canvas')
    canvas.width = image.naturalWidth
    canvas.height = image.naturalHeight
    const ctx = canvas.getContext('2d')
    if (!ctx) {
      throw new Error('浏览器不支持 2D Canvas，无法解码位图字体页')
    }
    ctx.imageSmoothingEnabled = false
    ctx.drawImage(image, 0, 0)
    return canvas
  } finally {
    URL.revokeObjectURL(url)
  }
}

function createEmptyPageCanvas(cols: number, rows: number, cellWidth: number, cellHeight: number): HTMLCanvasElement {
  const pageCanvas = document.createElement('canvas')
  pageCanvas.width = cols * cellWidth
  pageCanvas.height = rows * cellHeight
  const pageCtx = pageCanvas.getContext('2d')
  if (!pageCtx) {
    throw new Error('浏览器不支持 2D Canvas，无法创建位图字体页')
  }
  pageCtx.clearRect(0, 0, pageCanvas.width, pageCanvas.height)
  return pageCanvas
}

/**
 * 解码位图字体包纹理页，供预览与测试共用同一份 png。
 */
export async function loadBitmapFontPackagePages(pkg: BitmapFontPackage): Promise<HTMLCanvasElement[]> {
  const fileBase = toBitmapFontPackageKey(pkg.fontName, pkg.fontSize)
  const pageFiles = sortPackagePageFiles(pkg.files, fileBase)
  const pages: HTMLCanvasElement[] = []
  for (const pageFile of pageFiles) {
    pages.push(await bytesToCanvas(pageFile.bytes))
  }
  return pages
}

async function buildBitmapFontPackageIncremental(params: {
  existing: BitmapFontPackage
  sourceFontKey: string
  fontName: string
  nominalFontSize: number
  rasterFontSize: number
  fontFamily: string
  deltaText: string
}): Promise<BitmapFontPackage> {
  const existing = params.existing
  const fontName = params.fontName.trim() || params.fontFamily || 'sans-serif'
  const fontFamily = params.fontFamily.trim() || fontName
  const fontSize = clampBitmapFontSize(params.nominalFontSize)
  const rasterFontSize = params.rasterFontSize
  const aboveLine = existing.aboveLine || fontSize
  const { cols, rows, cellWidth, cellHeight } = existing
  if (!cols || !rows || !cellWidth || !cellHeight) {
    throw new Error('位图字体包缺少栅格参数，无法增量生成')
  }

  const slotsPerPage = cols * rows
  const fileBase = toBitmapFontPackageKey(fontName, fontSize)
  const pageFiles = sortPackagePageFiles(existing.files, fileBase)
  const pageCanvases: HTMLCanvasElement[] = []
  for (const pageFile of pageFiles) {
    pageCanvases.push(await bytesToCanvas(pageFile.bytes))
  }

  const glyphs = existing.glyphs.map((item) => new BitmapFontGlyph(item))
  const firstModifiedPage = Math.floor(glyphs.length / slotsPerPage)
  let glyphIndex = glyphs.length

  for (const char of Array.from(params.deltaText)) {
    const measured = measureChengguangBitmapGlyph(char, fontFamily, rasterFontSize)
    const pageIdx = Math.floor(glyphIndex / slotsPerPage)
    while (pageCanvases.length <= pageIdx) {
      pageCanvases.push(createEmptyPageCanvas(cols, rows, cellWidth, cellHeight))
    }
    const pageCanvas = pageCanvases[pageIdx]!
    let pageCtx = pageCanvas.getContext('2d')
    if (!pageCtx) {
      throw new Error('浏览器不支持 2D Canvas，无法绘制位图字体页')
    }
    const slotInPage = glyphIndex % slotsPerPage
    const row = Math.floor(slotInPage / cols)
    const col = slotInPage % cols
    if ((row + 1) * cellHeight > pageCanvas.height) {
      const grown = createEmptyPageCanvas(cols, row + 1, cellWidth, cellHeight)
      const grownCtx = grown.getContext('2d')
      if (!grownCtx) {
        throw new Error('浏览器不支持 2D Canvas，无法扩展位图字体页')
      }
      grownCtx.imageSmoothingEnabled = false
      grownCtx.drawImage(pageCanvas, 0, 0)
      pageCanvases[pageIdx] = grown
      pageCtx = grownCtx
    }
    paintGlyphInCell(
      pageCtx,
      char,
      col,
      row,
      cellWidth,
      cellHeight,
      aboveLine,
      rasterFontSize,
      fontFamily,
    )
    glyphs.push(new BitmapFontGlyph({
      char,
      index: glyphIndex,
      width: measured.width,
      height: resolveGlyphXfiHeight(measured.height, measured.underlineSize, aboveLine),
    }))
    glyphIndex += 1
  }

  const files: BitmapFontFile[] = []
  for (let pageIdx = 0; pageIdx < pageCanvases.length; pageIdx += 1) {
    const pageNumber = pageIdx + 1
    const pageCanvas = pageCanvases[pageIdx]!
    const reuseBytes = pageIdx < firstModifiedPage ? pageFiles[pageIdx]?.bytes : null
    files.push(new BitmapFontFile({
      fileName: `${fileBase}$${pageNumber}.png`,
      kind: 'page',
      mimeType: 'image/png',
      bytes: reuseBytes ?? await canvasToBytes(pageCanvas),
    }))
  }

  files.push(new BitmapFontFile({
    fileName: `${fileBase}.xfi`,
    kind: 'xfi',
    mimeType: 'application/octet-stream',
    bytes: buildXfiBytes({
      rows,
      cols,
      cellWidth,
      cellHeight,
      glyphs,
    }),
  }))

  return new BitmapFontPackage({
    fontName,
    fontSize,
    sourceFontKey: params.sourceFontKey,
    rows,
    cols,
    cellWidth,
    cellHeight,
    rasterFontSize,
    aboveLine,
    pageLayoutFixed: true,
    glyphs,
    files,
  })
}

async function buildBitmapFontPackageFull(params: {
  sourceFontKey: string
  fontName: string
  fontSize: number
  fontFamily: string
  mergedText: string
  isSaveLoad: boolean
}): Promise<BitmapFontPackage> {
  const fontName = params.fontName.trim() || params.fontFamily || 'sans-serif'
  const fontFamily = params.fontFamily.trim() || fontName
  const fontSize = clampBitmapFontSize(params.fontSize)

  const chars = Array.from(params.mergedText)
  const glyphs: BitmapFontGlyph[] = []
  let rasterFontSize = resolveBitmapFontRasterSize(fontSize)
  let measured = measurePackageGlyphs(chars, fontFamily, rasterFontSize, fontSize)
  if (params.isSaveLoad) {
    const slRaster = resolveBitmapFontRasterSize(fontSize, measured.cellWidth)
    if (slRaster !== rasterFontSize) {
      rasterFontSize = slRaster
      measured = measurePackageGlyphs(chars, fontFamily, rasterFontSize, fontSize)
    }
  }
  const cellWidth = measured.cellWidth
  const cellHeight = measured.cellHeight
  const aboveLine = measured.aboveLine
  const cols = Math.max(1, Math.floor(ATLAS_MAX_WIDTH / cellWidth))
  const rows = Math.max(1, Math.floor(ATLAS_MAX_HEIGHT / cellHeight))
  const leaveRows = Math.ceil(Math.max(1, chars.length) / cols)
  const drawRows = Math.max(1, leaveRows > rows ? rows : leaveRows)
  const fileBase = toBitmapFontPackageKey(fontName, fontSize)
  const files: BitmapFontFile[] = []
  let pageCanvas: HTMLCanvasElement | null = null
  let pageCtx: CanvasRenderingContext2D | null = null
  let dx = 0
  let dy = 0
  let page = 0

  const flushPage = async (): Promise<void> => {
    if (!pageCanvas) return
    files.push(new BitmapFontFile({
      fileName: `${fileBase}$${page}.png`,
      kind: 'page',
      mimeType: 'image/png',
      bytes: await canvasToBytes(pageCanvas),
    }))
    pageCanvas = null
    pageCtx = null
  }

  for (const [index, char] of chars.entries()) {
    if (!pageCanvas) {
      pageCanvas = createEmptyPageCanvas(cols, drawRows, cellWidth, cellHeight)
      pageCtx = pageCanvas.getContext('2d')
      if (!pageCtx) {
        throw new Error('浏览器不支持 2D Canvas，无法绘制位图字体页')
      }
      dx = 0
      dy = 0
      page += 1
    }
    paintGlyphInCell(
      pageCtx!,
      char,
      dx,
      dy,
      cellWidth,
      cellHeight,
      aboveLine,
      rasterFontSize,
      fontFamily,
    )
    const item = measured.glyphs[index]
    glyphs.push(new BitmapFontGlyph({
      char,
      index,
      width: item?.width || cellWidth,
      height: resolveGlyphXfiHeight(item?.height || cellHeight, item?.underlineSize || 0, aboveLine),
    }))
    dx += 1
    if (dx >= cols) {
      dx = 0
      dy += 1
      if (dy >= rows) {
        await flushPage()
      }
    }
  }
  await flushPage()

  files.push(new BitmapFontFile({
    fileName: `${fileBase}.xfi`,
    kind: 'xfi',
    mimeType: 'application/octet-stream',
    bytes: buildXfiBytes({
      rows,
      cols,
      cellWidth,
      cellHeight,
      glyphs,
    }),
  }))

  return new BitmapFontPackage({
    fontName,
    fontSize,
    sourceFontKey: params.sourceFontKey,
    rows,
    cols,
    cellWidth,
    cellHeight,
    rasterFontSize,
    aboveLine,
    pageLayoutFixed: true,
    glyphs,
    files,
  })
}

/** 同字体源与包名：所需字符齐全则跳过；仅补新字且不撑大格子则增量。 */
async function buildBitmapFontPackage(params: {
  sourceFontKey: string
  fontName: string
  fontSize: number
  fontFamily: string
  mergedText: string
  isSaveLoad: boolean
  existing?: BitmapFontPackage | null
}): Promise<BitmapFontPackage> {
  const fontName = params.fontName.trim() || params.fontFamily || 'sans-serif'
  const fontFamily = params.fontFamily.trim() || fontName
  const fontSize = clampBitmapFontSize(params.fontSize)
  const sourceFontKey = params.sourceFontKey.trim()
  const existing = params.existing
  const rasterFontSize = existing && params.isSaveLoad
    ? existing.rasterFontSize
    : resolveBitmapFontRasterSize(fontSize)

  if (existing && canReuseBitmapFontPackage(existing, sourceFontKey, fontName, fontSize, rasterFontSize)) {
    const missing = listMissingChars(params.mergedText, existing.glyphs)
    if (!missing) return existing
    if (charsNeedLargerCell(missing, existing, fontFamily, rasterFontSize)) {
      return buildBitmapFontPackageFull({
        sourceFontKey,
        fontName,
        fontSize,
        fontFamily,
        mergedText: params.mergedText,
        isSaveLoad: params.isSaveLoad,
      })
    }
    return buildBitmapFontPackageIncremental({
      existing,
      sourceFontKey,
      fontName,
      nominalFontSize: fontSize,
      rasterFontSize,
      fontFamily,
      deltaText: missing,
    })
  }

  return buildBitmapFontPackageFull({
    sourceFontKey,
    fontName,
    fontSize,
    fontFamily,
    mergedText: params.mergedText,
    isSaveLoad: params.isSaveLoad,
  })
}

function disposePackageFiles(pack: BitmapFontPackage): void {
  for (const file of pack.files) {
    file.revokeObjectUrl()
  }
}

/**
 * 测试/发布前生成工程位图字体数据。
 * 剧情包使用全文集；存读档独立包使用常用符号 + 剧情名；规格一致时共用 xfi。
 */
export async function buildBitmapFontData(params: {
  project: MainProject
  currentRoot: DataFileMap | null
  extraText?: string
  existingBitmapFont?: BitmapFontData | null
}): Promise<BitmapFontData> {
  const project = params.project
  const previous = params.existingBitmapFont
  const nextExtraText = uniqueChars([previous?.extraText ?? '', params.extraText ?? ''].join(''))
  const mergedText = collectMergedBitmapFontText({
    project,
    previousExtraText: previous?.extraText,
    extraText: params.extraText,
  })
  const mainSpec = resolveMainFontSpec(project, params.currentRoot)
  const mainPackageKey = toBitmapFontPackageKey(mainSpec.packageFontName, mainSpec.fontSize)
  const saveLoadText = uniqueChars(
    [
      collectSaveLoadBitmapFontText(project),
      previous?.extraText ?? '',
      params.extraText ?? '',
    ].join(''),
  )
  const targets = collectBitmapFontTargets(project, params.currentRoot)
  const packages: BitmapFontPackage[] = []

  for (const target of targets) {
    const sourceFontKey = String(target.sourceFontName || '').trim()
    const packageKey = toBitmapFontPackageKey(target.packageFontName, target.fontSize)
    const existingPkg =
      previous?.packages.find(
        (item) =>
          item.sourceFontKey === sourceFontKey
          && toBitmapFontPackageKey(item.fontName, item.fontSize) === packageKey,
      ) ?? null
    const fontFamily = params.currentRoot
      ? (await ensureFontLoadedFromVirtualResource({
          root: params.currentRoot,
          storedPath: target.sourceFontName,
        })).family
      : target.sourceFontName || target.packageFontName || 'sans-serif'
    const textForPackage = packageKey === mainPackageKey ? mergedText : saveLoadText
    const nextPkg = await buildBitmapFontPackage({
      sourceFontKey,
      fontName: target.packageFontName,
      fontSize: target.fontSize,
      fontFamily,
      mergedText: textForPackage,
      isSaveLoad: packageKey !== mainPackageKey,
      existing: existingPkg,
    })
    if (existingPkg && nextPkg !== existingPkg) {
      disposePackageFiles(existingPkg)
    }
    packages.push(nextPkg)
  }

  for (const orphan of previous?.packages ?? []) {
    if (packages.includes(orphan)) continue
    disposePackageFiles(orphan)
  }
  for (const file of previous?.extraFiles ?? []) {
    file.revokeObjectUrl()
  }

  return new BitmapFontData({
    packages,
    extraFiles: [
      new BitmapFontFile({
        fileName: 'font.list',
        kind: 'meta',
        mimeType: 'application/octet-stream',
        bytes: buildFontListBytes(project, targets, params.currentRoot),
      }),
      new BitmapFontFile({
        fileName: 'saveload.list',
        kind: 'meta',
        mimeType: 'application/octet-stream',
        bytes: buildSaveLoadListBytes(project, targets, params.currentRoot),
      }),
    ],
    extraText: nextExtraText,
  })
}
